/**
 * SC-209: the bridge between the room and the floating window.
 *
 * Two jobs, and they are separate on purpose. It turns the controller's raw inputs — LiveKit participants,
 * tracks, the proximity predicates, the whiteboard store — into the `PipModel`, and publishes that model on
 * every change whether or not a window is open (§2.3). Then, when there is a window, it portals the UI into
 * it.
 *
 * The portal is why the model is built here rather than inside the window: a portal keeps its subtree in the
 * main React tree, so `PipRoot` inherits Redux, the LiveKit `RoomContext` and both theme providers, and the
 * only thing that is somewhere else is the pixels. React attaches its event listeners to a portal's container
 * as well as its root, which is what makes a click inside another document reach these handlers at all.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CacheProvider } from '@emotion/react'
import { StyleSheetManager } from 'styled-components'

import type { VideoControlSurface } from '../types/LiveKitVideoDisplay.types'
import { compareSidebarParticipants } from '../utils/sidebarOrder'
import {
  useLocalCameraWaiting,
  useRemoteCameraWaitingIds,
} from '../components/useCameraTilePicture'

import {
  PIP_TILE_CAP,
  liveShares,
  liveWhiteboards,
  pipCameraState,
  resolvePipContent,
  selectPipTiles,
} from './pipModel'
import type { PipContent, PipModel, PipTile, PipWhiteboardContent } from './pipModel'
import { pipAutoOpenBlocked } from './pipPreferences'
import { holdPreviewTrackRef } from './renderers/VideoPipRenderer'
import {
  readLocalCameraHoldPreview,
  subscribeLocalCameraHoldPreview,
} from '../../LiveKitRoomConnectorBackground'
import { PipRoot } from './PipRoot'
import { remoteCameraOffPicture, useLocalCameraOffPicture } from '../cameraOffPicture'
import type { PictureInPictureApi } from './usePictureInPicture'

export interface PipHostProps {
  api: PictureInPictureApi
  /** The same inputs `SidebarVideoDisplay` receives, so the two agree about who is in the room. */
  participants: any[]
  screenShareTracks: any[]
  tracks: any[]
  speakingIds: Set<string>
  isParticipantVisibleWithGrace: (id: string) => boolean
  isParticipantInSameRoom: (id: string) => boolean
  videoDisplayState: {
    audioEnabled: boolean
    videoEnabled: boolean
    isAutoMuted: boolean
    autoMuteReason: string
    maximizedScreenShare: { participantId: string } | null
    maximizedWhiteboard: { whiteboardId: string; participantId: string; whiteboardUrl: string } | null
  }
  videoDisplayActions: {
    toggleAudio: () => void
    toggleVideo: () => void
  }
  roomName: string
  /** `state.whiteboard.*`, passed in rather than selected here so this stays testable without a store. */
  activeWhiteboardUsers: Record<string, string[]>
  whiteboardVrIds: Record<string, string>
  whiteboardUrls: Record<string, string>
  localVrId: string | null
  /**
   * SC-415: the call bar's own raise-hand control, absent exactly when the call bar hides it. Passed in
   * rather than read from `useSharedRaisedHands`, whose provider-less fallback reports `canRaiseHand: true`.
   */
  raiseHand?: VideoControlSurface['raiseHand']
  /** SC-415: session ids (= LiveKit identities) with a raised hand, the same set the sidebar badges. */
  handRaisedSessionIds: ReadonlySet<string>
}

/**
 * Nothing below this line runs for a browser that cannot do picture-in-picture, or a workspace that has the
 * switch off — which today is most of them. The real work is in `PipHostView`, mounted only when there is
 * something for it to do, so that an office which never turns this on pays nothing for it: no model built on
 * every proximity tick, no whiteboard bookkeeping, no `localStorage` read when somebody shares.
 *
 * A thin wrapper rather than an early return inside the component, because the hooks below have to run
 * unconditionally once they run at all.
 */
export function PipHost(props: PipHostProps) {
  if (props.api.mode === 'none') return null
  return <PipHostView {...props} />
}

function PipHostView({
  api,
  participants,
  screenShareTracks,
  tracks,
  speakingIds,
  isParticipantVisibleWithGrace,
  isParticipantInSameRoom,
  videoDisplayState,
  videoDisplayActions,
  roomName,
  activeWhiteboardUsers,
  whiteboardVrIds,
  whiteboardUrls,
  localVrId,
  raiseHand,
  handRaisedSessionIds,
}: PipHostProps) {
  const { publishModel, close, open, surface, isOpen, openReason } = api

  // `useVideoDisplayState` rebuilds its state and action objects on every render, so depending on the
  // containers would rebuild the model — and re-render the window — on every proximity tick. The
  // controller's own `controlSurface` memo takes the same precaution, for the same reason.
  const {
    audioEnabled,
    videoEnabled,
    isAutoMuted,
    autoMuteReason,
    maximizedScreenShare,
    maximizedWhiteboard,
  } = videoDisplayState
  const { toggleAudio, toggleVideo } = videoDisplayActions
  const canRaiseHand = Boolean(raiseHand)
  const localHandRaised = Boolean(raiseHand?.raised)
  const toggleHandRaise = raiseHand?.toggle
  const localPicture = useLocalCameraOffPicture()

  const sharerIds = useMemo(
    () => new Set<string>(screenShareTracks.map((track) => track.participant.identity)),
    [screenShareTracks]
  )

  const displayNameFor = useCallback(
    (participantId: string) =>
      participants.find((p) => p.identity === participantId)?.name || participantId.slice(0, 8),
    [participants]
  )

  /**
   * The same filter and the same sort the sidebar applies: you are always in it, everyone else has to be
   * both close enough and in the same virtual room, and SC-415 raised hands come ahead of the mic tiebreak.
   * `selectPipTiles` then pins you, sharers and raised hands, so the sort only decides who is next when the
   * four slots run out.
   */
  const present = useMemo(() => {
    return participants
      .filter((p) => p.isLocal || (isParticipantVisibleWithGrace(p.identity) && isParticipantInSameRoom(p.identity)))
      .sort(compareSidebarParticipants(sharerIds, handRaisedSessionIds))
  }, [
    participants,
    isParticipantVisibleWithGrace,
    isParticipantInSameRoom,
    sharerIds,
    handRaisedSessionIds,
  ])

  // SC-158: who is held for their background, for the model. The tiles read the same sources themselves;
  // this copy is for Safari, whose single video has no tile to cover and must never be handed one of these.
  const { waiting: localCameraWaiting } = useLocalCameraWaiting(true, videoEnabled)
  const remoteIds = useMemo(
    () => present.filter((p) => !p.isLocal).map((p) => p.identity as string),
    [present]
  )
  const remoteCameraWaitingIds = useRemoteCameraWaitingIds(remoteIds)

  // SC-158, Safari: our own live picture while a None-to-effect switch holds the camera. Plain state rather
  // than `useSyncExternalStore`, deliberately: it must render in the same batch as the hold state it comes
  // and goes with, or the window would see our camera held with nothing to show and switch away for a
  // frame. One reference per clone, so the renderer sees the same track until it changes.
  const [holdPreview, setHoldPreview] = useState<MediaStreamTrack | null>(
    readLocalCameraHoldPreview
  )
  useEffect(() => {
    const update = () => setHoldPreview(readLocalCameraHoldPreview())
    update()
    return subscribeLocalCameraHoldPreview(update)
  }, [])
  const holdPreviewRef = useMemo(
    () => (holdPreview ? holdPreviewTrackRef(holdPreview) : undefined),
    [holdPreview]
  )

  /**
   * D2's memory. Only ever consulted while the window is open, which is what makes the cell hold "whatever
   * was visible when it opened" without anything here needing to know when that was: closed, the model
   * mirrors the tab; open, it holds.
   */
  const heldContentRef = useRef<PipContent>(null)

  /**
   * Every board the tab has opened this session. A public board is only offered to the window once the tab
   * has shown it, and this is what keeps it offered afterwards — so minimising the board in the tab does
   * not empty a window that is holding it. State rather than a ref, so the content memo can depend on it.
   */
  const [everVisibleBoards, setEverVisibleBoards] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    const id = maximizedWhiteboard?.whiteboardId
    if (!id || everVisibleBoards.has(id)) return
    setEverVisibleBoards((prev) => new Set(prev).add(id))
  }, [maximizedWhiteboard, everVisibleBoards])

  // Nothing here forgets the hold on a LiveKit reconnect, on purpose. A signal resume keeps every participant
  // and publication, and `resolvePipContent` re-reads the held source by identity from the live list on every
  // resolve, so the hold is never a dead reference; clearing it only made the window jump to another source.
  // A full reconnect tears the participants down in the SDK, and the held share then ends like any other.

  const content = useMemo<PipContent>(() => {
    const shares = liveShares(screenShareTracks, displayNameFor)

    const visibleWhiteboard: PipWhiteboardContent | null = maximizedWhiteboard
      ? {
          kind: 'whiteboard',
          whiteboardId: maximizedWhiteboard.whiteboardId,
          url: maximizedWhiteboard.whiteboardUrl,
          participantId: maximizedWhiteboard.participantId,
          participantName: displayNameFor(maximizedWhiteboard.participantId),
          orientation: 'landscape',
        }
      : null

    const whiteboards = liveWhiteboards({
      activeWhiteboardUsers,
      whiteboardVrIds,
      whiteboardUrls,
      localVrId,
      visible: visibleWhiteboard,
      everVisible: everVisibleBoards,
      displayNameFor,
    })

    const resolved = resolvePipContent(
      // Closed, there is nothing to hold: the model mirrors the tab so that the first resolve after the
      // click already *is* "what was visible when the window opened".
      isOpen ? heldContentRef.current : null,
      {
        share: maximizedScreenShare
          ? shares.find((share) => share.participantId === maximizedScreenShare.participantId) ?? null
          : null,
        whiteboard: visibleWhiteboard,
      },
      { shares, whiteboards }
    )

    return resolved
  }, [
    screenShareTracks,
    displayNameFor,
    maximizedScreenShare,
    maximizedWhiteboard,
    activeWhiteboardUsers,
    whiteboardVrIds,
    whiteboardUrls,
    localVrId,
    everVisibleBoards,
    isOpen,
  ])

  // Written after the commit, never during the render that produced it: a memo that mutates a ref is not a
  // pure function of its dependencies, and React may invoke it twice (StrictMode) or throw a render away
  // (concurrent restart) — either of which would advance D2's hold for a render nobody ever saw.
  useEffect(() => {
    heldContentRef.current = content
  }, [content])

  const model = useMemo<PipModel>(() => {
    const localId = present.find((p) => p.isLocal)?.identity ?? ''
    const order = present.map((p) => p.identity)
    const { tileIds, overflow } = selectPipTiles({
      order,
      speakingIds,
      localId,
      sharerIds,
      handRaisedIds: handRaisedSessionIds,
      cap: PIP_TILE_CAP,
    })

    const byId = new Map<string, any>(present.map((p) => [p.identity, p]))

    const tileFor = (id: string): PipTile => {
      const participant = byId.get(id)
      const isLocal = Boolean(participant?.isLocal)

      // `cameraTracks` is filtered by room membership and nothing else, so it still holds a reference for a
      // camera that has been switched off — the publication survives, muted. Handing that to `VideoTrack`
      // leaves the last frame frozen on screen, which reads as a broken tile rather than as a camera that is
      // off. The state decides; the track only travels when it is live.
      const trackRef = tracks.find((track) => track.participant.identity === id)
      const cameraState = pipCameraState({
        isLocal,
        localVideoEnabled: videoEnabled,
        remoteCameraEnabled: Boolean(participant?.isCameraEnabled),
        publicationMuted: Boolean(trackRef?.publication?.isMuted),
        hasTrack: Boolean(trackRef?.publication?.track),
      })

      return {
        participantId: id,
        isLocal,
        isSpeaking: speakingIds.has(id),
        micEnabled: isLocal ? audioEnabled : Boolean(participant?.isMicrophoneEnabled),
        cameraState,
        cameraTrack: cameraState === 'live' ? trackRef : undefined,
        cameraWaiting: isLocal ? localCameraWaiting : remoteCameraWaitingIds.has(id),
        heldPreview: isLocal && localCameraWaiting ? holdPreviewRef : undefined,
        displayName: participant?.name || id.slice(0, 8),
        handRaised: handRaisedSessionIds.has(id),
        picture: isLocal
          ? localPicture
          : remoteCameraOffPicture({ identity: id, name: participant?.name, metadata: participant?.metadata }),
      }
    }
    const tiles: PipTile[] = tileIds.map(tileFor)

    // Safari only. Its window keeps the place of whoever it shows through a hold (`keptPlace`), which it can
    // only do for someone it can still see: with five or more people present a promotion can push them out
    // of the four slots. So every remote outside the slots is handed over, not only those held now: the
    // person shown has to be found again after the hold ends, and in the gap before its "waiting" message
    // arrives, when they are no longer (or not yet) waiting. Handed over separately rather than by raising
    // the cap, which would cost the document window a slot it does not have.
    const keep =
      api.mode === 'video'
        ? remoteIds.filter((id) => !tileIds.includes(id)).map(tileFor)
        : undefined

    return {
      tiles,
      keep: keep && keep.length > 0 ? keep : undefined,
      overflow,
      proximityCount: order.length,
      content,
      roomName,
      controls: {
        audioEnabled,
        videoEnabled,
        isAutoMuted,
        autoMuteReason,
        // Wrapped so the toolbar's click event is never passed through as an argument.
        toggleAudio: () => toggleAudio(),
        toggleVideo: () => toggleVideo(),
        raiseHand:
          canRaiseHand && toggleHandRaise
            ? { raised: localHandRaised, toggle: () => toggleHandRaise() }
            : undefined,
      },
    }
  }, [
    api.mode,
    remoteIds,
    present,
    speakingIds,
    sharerIds,
    tracks,
    localCameraWaiting,
    remoteCameraWaitingIds,
    holdPreviewRef,
    content,
    audioEnabled,
    videoEnabled,
    isAutoMuted,
    autoMuteReason,
    toggleAudio,
    toggleVideo,
    canRaiseHand,
    localHandRaised,
    toggleHandRaise,
    handRaisedSessionIds,
    roomName,
    localPicture,
  ])

  useEffect(() => {
    publishModel(model)
  }, [publishModel, model])

  const localId = model.tiles.find((tile) => tile.isLocal)?.participantId
  const localShare = localId
    ? screenShareTracks.find((track) => track.participant.identity === localId)
    : undefined
  const localIsSharing = Boolean(localShare)
  // The captured `LocalTrack`; LiveKit always sets it on a local publication.
  const localShareTrack: unknown = localShare?.publication?.track

  /**
   * One attempt per share, not one per render: a refusal is not something to keep asking about.
   *
   * "The share" is the captured track, not the share's presence in the room. A full LiveKit reconnect
   * unpublishes and republishes every local track (`republishAllTracks`) — the same `LocalTrack` in a new
   * publication — and the app's own `screenShareEnabled` follows that publication down and back up too, so
   * resetting on either would ask again for a share the user never stopped. A share the user starts again
   * captures a new track, which is what earns it its own attempt. `null` until the first attempt.
   */
  const triedForShareTrackRef = useRef<unknown>(null)

  /**
   * R14: Document mode only (Safari never opens by itself, see below); best-effort even on Chrome.
   *
   * `toggleScreenShare` is synchronous — it flips state and dispatches an event, and LiveKit calls
   * `getDisplayMedia()` several tasks later — so by the time a share track exists the click's transient
   * activation is long gone and `requestWindow()` may simply be refused. On Chrome and Edge, switching away
   * from the tab can still open it (`usePictureInPicture`, R2), but only where the site is allowed to. The
   * toolbar button is the fallback, and it always works.
   *
   * Wrapping `toggleScreenShare` to open synchronously would be worse, not better: it would fire before the
   * user has even chosen a window to share.
   */
  useEffect(() => {
    // Safari's single-video API needs a user gesture and an element that has loaded, and here there is
    // neither: the share has only just reached its element (`readyState` 0) and the click is long gone. It
    // would be refused every time, so Safari never opens by itself — vault "Safari, which is one video and
    // nothing else".
    if (api.mode === 'video') return
    if (!localIsSharing) return
    if (isOpen) {
      // A window is already up, possibly still holding another source (D2's hold) rather than this share.
      // Closing it later (its Close, "Back to tab") is a refusal, not a cue to reopen for this share, so the
      // share counts as asked.
      triedForShareTrackRef.current = localShareTrack
      return
    }
    if (triedForShareTrackRef.current === localShareTrack) return
    // `open` enforces this too, but it has to be checked here as well, before the share is marked as asked:
    // a share skipped because the toggle was off has not been asked, and the bookkeeping must not say so.
    if (pipAutoOpenBlocked()) return

    triedForShareTrackRef.current = localShareTrack
    open('share-started')
  }, [api.mode, localIsSharing, localShareTrack, isOpen, open])

  const backToTab = useCallback(() => {
    // Raise the tab and take the window down with it — "back to tab" is one action, not two.
    window.focus()
    close()
  }, [close])

  if (!surface) return null

  return createPortal(
    <CacheProvider value={surface.emotionCache}>
      <StyleSheetManager target={surface.document.head}>
        <PipRoot
          model={model}
          width={surface.width}
          height={surface.height}
          onBackToTab={backToTab}
          onClose={close}
          openReason={openReason}
        />
      </StyleSheetManager>
    </CacheProvider>,
    surface.document.body
  )
}

export default PipHost
